export default function initCookieConsent() {
    Alpine.data('cookieConsent', () => ({
        consent: null,
        showCookieBar: false,
        showGroups: false,
        groupsAreHidden: true,

        init() {
            const form = this.$refs.consentForm as HTMLFormElement;
            getSessionInfo().then((session) => {
                const csrfInput = this.$refs.csrfToken as HTMLInputElement;
                csrfInput.value = session.csrfTokenValue;
            });
            getConsent(form.dataset.show ?? '').then((consent) => {
                this.consent = consent;
                if (this.consent == null) {
                    this.showCookieBar = true;
                } else {
                    const necessaryCheck = this.$refs.necessary as HTMLInputElement|null;
                    if (necessaryCheck && !necessaryCheck.disabled) {
                        necessaryCheck.checked = consent.necessary;
                    }

                    const statisticsCheck = this.$refs.statistics as HTMLInputElement|null;
                    if (statisticsCheck && !statisticsCheck.disabled) {
                        statisticsCheck.checked = consent.statistics;
                    }

                    const marketingCheck = this.$refs.marketing as HTMLInputElement|null;
                    if (marketingCheck && !marketingCheck.disabled) {
                        marketingCheck.checked = consent.marketing;
                    }
                }
                addWindowObject(this.consent);
            });

            window.addEventListener('cookie-consent:open', () => {
                this.showCookieBar = true;
                this.showGroups = true;
                this.groupsAreHidden = false;
            });

            this.$watch('showCookieBar', () => {
                if (this.showCookieBar) {
                    this.$root.setAttribute('role', 'dialog');
                    const acceptBtn = document.querySelector('button[data-action="accept"]') as HTMLButtonElement | null;
                    const disclaimer = document.getElementById('disclaimer');
                    if (acceptBtn && (disclaimer === null || !disclaimer.checkVisibility())) {
                        this.$nextTick(() => acceptBtn.focus());
                    }
                } else {
                    this.$root.removeAttribute('role');
                    this.$nextTick(() => resetFocus());
                }
            });
        },

        toggleGroups() {
            this.showGroups = !this.showGroups;
            this.groupsAreHidden = !this.groupsAreHidden;
        },

        handleConsentSubmit(e: SubmitEvent) {
            e.preventDefault();
            this.showCookieBar = false;
            const submitBtn = e.submitter;
            const form = this.$refs.consentForm as HTMLFormElement;
            let data = serialize(form);
            const checkboxes = form.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;
            if (submitBtn?.dataset.action === 'accept') {
                data = data.concat('&acceptAll=true');
                checkboxes.forEach((checkbox) => {
                    if (!checkbox.checked) {
                        checkbox.checked = true;
                    }
                });
            } else if (submitBtn?.dataset.action === 'decline') {
                checkboxes.forEach((checkbox) => {
                    if (checkbox.checked && !checkbox.disabled) {
                        const checkboxName = encodeURIComponent(checkbox.name);
                        const regex = new RegExp(`([&?]${checkboxName}=)on\\b`);
                        data = data.replace(regex, '$1off');
                        checkbox.checked = false;
                    }
                });
            }

            const xhr = new XMLHttpRequest();
            xhr.open('POST', form.dataset.url ?? '');
            xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
            xhr.send(data);

            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    getConsent(form.dataset.show ?? '').then((consent) => {
                        this.consent = consent;
                        addWindowObject(this.consent);
                        if (this.$refs.success) {
                            this.$refs.success.classList.remove('hidden');
                        }
                    });
                    if (xhr.status !== 200) console.log('Error: ' + xhr.status);
                    else if (form.hasAttribute('data-refresh') && form.dataset.refresh === 'true' && form.hasAttribute('data-refreshtime')) {
                        refresh(parseInt(form.dataset.refreshtime ?? '2000'));
                    }
                }
            }
        },
    }))
}

const getConsent = (showUrl: string) => {
    return fetch(showUrl, {
        headers: {
            Accept: 'application/json',
        },
    }).then((response) => response.json())
};

const getSessionInfo = () => {
    return fetch('/actions/users/session-info/', {
        headers: {
            Accept: 'application/json',
        },
    }).then((response) => response.json());
};

/*!
 * Serialize all form data into a query string
 * (c) 2018 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param  {Node}   form The form to serialize
 * @return {String}      The serialized form data
 */
const serialize = function (form: HTMLFormElement): string {
    const serialized: string[] = [];

    for (let i = 0; i < form.elements.length; i++) {
        const field = form.elements[i] as HTMLInputElement | HTMLSelectElement;

        if (!field.name || field.disabled || field.type === 'file' || field.type === 'reset' || field.type === 'button') continue;

        if (field instanceof HTMLSelectElement && field.type === 'select-multiple') {
            for (let n = 0; n < field.options.length; n++) {
                if (field.options[n].selected) {
                    serialized.push(`${encodeURIComponent(field.name)}=${encodeURIComponent(field.options[n].value)}`);
                }
            }
        } else if ((field.type !== 'checkbox' && field.type !== 'radio') || field.checked) {
            serialized.push(`${encodeURIComponent(field.name)}=${encodeURIComponent(field.value)}`);
        }
    }

    return serialized.join('&');
};

function refresh(ms: number) {
    setTimeout(function() {
        location.reload();
    }, ms);
}

function addWindowObject(consent: any) {
    if (consent === 'null' || consent === null) {
        window.ccc = {};
    } else {
        window.ccc = consent;
        runTracking();
    }

    window.dispatchEvent(new CustomEvent('cookie-consent:updated', {
        detail: { consent: globalThis.ccc },
    }));
}

function resetFocus() {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
        activeElement.blur();
    }

    // Move focus to a neutral root target so the next Tab starts from the top.
    document.body.setAttribute('tabindex', '-1');
    document.body.focus({ preventScroll: true });
    document.body.removeAttribute('tabindex');
}

const runTracking = () => {
    if (window.trackingRan || typeof dataLayer === 'undefined') {
        return;
    }

    if (window.ccc !== null && window.ccc.necessary === true) {
        dataLayer.push({ 'event': 'cookieconsent_necessary' });
    }

    if (window.ccc !== null && window.ccc.statistics === true) {
        dataLayer.push({ 'event': 'cookieconsent_statistics' });
    }

    if (window.ccc !== null && window.ccc.marketing === true) {
        dataLayer.push({ 'event': 'cookieconsent_marketing' });
    }

    window.trackingRan = true;
}